import React, { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon, Text, useTheme } from '@Sage/hoi-poi-ui';

import AbsoluteLoader from 'components/AbsoluteLoader';
import { CAMPAIGNS_TEMPLATES } from 'constants/Entities';
import { getMarketingEmailTemplates } from 'services/CampaignsService';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';

import TemplatesGrid from './components/TemplatesGrid';
import { MARKETING_TEMPLATES_PATHS } from '../marketingTemplatesPaths';
import { useMarketingTemplatesRefresh } from '../MarketingTemplatesRefreshContext';

import './styles.scss';

const MarketingTemplatesCreate = memo(({ footerPreview, onOpenHtmlEditor, onOpenVisualEditor }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const searchQuery = useSelector(
        (state) =>
            state.entityFilters?.[CAMPAIGNS_TEMPLATES.entity]?.filters?.matchingName?.value ?? '',
    );

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    const normalizedSearchQuery = (searchQuery || '').trim();

    const refetchTemplates = useCallback(() => {
        setLoading(true);
        getMarketingEmailTemplates(normalizedSearchQuery)
            .then((data) => {
                setTemplates(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                errorToast({ text: getLiteral('error_generalerror') });
                setTemplates([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [normalizedSearchQuery]);

    useEffect(() => {
        refetchTemplates();
    }, [refetchTemplates]);

    const { refreshNonce } = useMarketingTemplatesRefresh();

    useEffect(() => {
        if (refreshNonce === 0) {
            return;
        }
        refetchTemplates();
    }, [refreshNonce, refetchTemplates]);

    const handleBack = useCallback(() => {
        navigate(MARKETING_TEMPLATES_PATHS.BASE_LIST);
    }, [navigate]);

    const handleSelectScratchVisual = useCallback(() => {
        onOpenVisualEditor?.();
    }, [onOpenVisualEditor]);

    const handleSelectScratchHtml = useCallback(() => {
        onOpenHtmlEditor?.();
    }, [onOpenHtmlEditor]);

    const handleUsePredefined = useCallback(
        (row) => {
            const jsonRaw = row?.strJson;
            const jsonStr = typeof jsonRaw === 'string' ? jsonRaw.trim() : '';
            const looksLikeTopolJson = jsonStr.startsWith('{') || jsonStr.startsWith('[');

            if (looksLikeTopolJson) {
                onOpenVisualEditor?.({
                    strJson: jsonRaw,
                    initialTitle: row?.name ?? '',
                    // Do NOT pass templateId here: this flow creates a NEW template from a predefined one
                });
                return;
            }

            onOpenHtmlEditor?.({
                initialHtml: row?.strBody ?? '',
                initialTitle: row?.name ?? '',
                // Do NOT pass templateId here: this flow creates a NEW template from a predefined one
            });
        },
        [onOpenHtmlEditor, onOpenVisualEditor],
    );

    return (
        <div className="marketing-templates-create">
            <div className="marketing-templates-create__toolbar">
                <button
                    type="button"
                    className="marketing-templates-create__back"
                    onClick={handleBack}
                    aria-label={getLiteral('action_back')}
                >
                    <Icon name="arrowBack" color={theme.colors.actionMajor[500]} />
                </button>
                <Text type="h5" bold className="marketing-templates-create__title">
                    {getLiteral('label_marketing_campaigns_templates_select_title')}
                </Text>
            </div>

            <div className="marketing-templates-create__body">
                {loading ? (
                    <div className="marketing-templates-create__loading">
                        <AbsoluteLoader size="huge" />
                    </div>
                ) : (
                    <TemplatesGrid
                        footerPreview={footerPreview}
                        templates={templates}
                        onSelectScratchHtml={handleSelectScratchHtml}
                        onSelectScratchVisual={handleSelectScratchVisual}
                        onUsePredefined={handleUsePredefined}
                    />
                )}
            </div>
        </div>
    );
});

MarketingTemplatesCreate.displayName = 'MarketingTemplatesCreate';

MarketingTemplatesCreate.propTypes = {
    footerPreview: PropTypes.shape({
        senderName: PropTypes.string,
        senderAddress: PropTypes.string,
        footerHere: PropTypes.string,
        linkHref: PropTypes.string,
    }),
    onOpenHtmlEditor: PropTypes.func,
    onOpenVisualEditor: PropTypes.func,
};

export default MarketingTemplatesCreate;
